import { permissionKey } from './types.js';

/**
 * WHICH EMPLOYEE FIELDS EACH "SENSITIVE INFORMATION" MODULE COVERS.
 *
 * The single source of truth shared by the API (redaction, write refusal) and
 * the web (restricted state, payload omission). The API test asserts that this
 * list and `ENCRYPTED_EMPLOYEE_STRING_FIELDS` agree exactly, so a new encrypted
 * column cannot ship without being assigned to a group.
 *
 * SALARY is the odd one out: text can be masked to its last four, money cannot,
 * so a restricted salary is WITHHELD (null in the payload, the group named in
 * `restrictedFields`) and the UI says "Restricted" — never 0, never a blank.
 * The salary list must equal `ENCRYPTED_EMPLOYEE_SALARY_FIELDS`.
 */
export const SENSITIVE_FIELD_GROUPS = {
  bank: {
    module: 'sensitive.bank',
    label: 'Bank details',
    fields: ['bankName', 'bankAccountNumber', 'bankIban'],
  },
  identity: {
    module: 'sensitive.identity',
    label: 'ID & passport numbers',
    fields: ['passportNumber', 'emiratesIdNumber', 'visaNumber', 'laborCardNumber'],
  },
  salary: {
    module: 'sensitive.salary',
    label: 'Salary & pay figures',
    fields: ['basicSalary', 'housingAllowance', 'transportAllowance', 'otherAllowance', 'totalSalary'],
  },
} as const;

/** Groups whose restricted value is withheld (null) rather than masked to its last four. */
export const WITHHELD_GROUPS: readonly SensitiveGroup[] = ['salary'];
export const isWithheldGroup = (g: SensitiveGroup): boolean => WITHHELD_GROUPS.includes(g);

export type SensitiveGroup = keyof typeof SENSITIVE_FIELD_GROUPS;

export const SENSITIVE_GROUPS = Object.keys(SENSITIVE_FIELD_GROUPS) as SensitiveGroup[];

/** Every field covered by any group. */
export const ALL_SENSITIVE_FIELDS: readonly string[] = SENSITIVE_GROUPS.flatMap(
  (g) => SENSITIVE_FIELD_GROUPS[g].fields
);

export const sensitiveViewKey = (g: SensitiveGroup): string =>
  permissionKey(SENSITIVE_FIELD_GROUPS[g].module, 'view');
export const sensitiveEditKey = (g: SensitiveGroup): string =>
  permissionKey(SENSITIVE_FIELD_GROUPS[g].module, 'edit');

/** The groups a permission set may NOT read. */
export function groupsWithoutView(held: { has(k: string): boolean }): SensitiveGroup[] {
  return SENSITIVE_GROUPS.filter((g) => !held.has(sensitiveViewKey(g)));
}

/** The groups whose fields appear (with a value, even null) in `body`. */
export function groupsTouchedBy(body: unknown): SensitiveGroup[] {
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  return SENSITIVE_GROUPS.filter((g) =>
    SENSITIVE_FIELD_GROUPS[g].fields.some((f) => b[f] !== undefined)
  );
}

/**
 * Document types whose scan IS an identity number (the passport page, the
 * Emirates ID card). Their files may only be opened by the owner or someone
 * granted `sensitive.identity.view`; the row itself (name, expiry) stays visible.
 */
export const IDENTITY_DOCUMENT_TYPES: readonly string[] = ['PASSPORT', 'EMIRATES_ID', 'VISA', 'LABOR_CARD'];

export const isIdentityDocumentType = (type: unknown): boolean =>
  typeof type === 'string' && IDENTITY_DOCUMENT_TYPES.includes(type);
